import { useState } from "react";
import { Plus, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Dropdown whose option list can be edited in place (chips with ×, plus an add box).
// onSaveList(next) persists and resolves to the saved list (or null on failure).
export const PickListField = ({
  label,
  testKey,
  value,
  onChange,
  options,
  onSaveList,
  placeholder = "Add an option",
  normalize = (v) => v,
  minOne = "Keep at least one option",
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const add = async () => {
    const v = normalize(draft.trim());
    if (!v) return;
    const saved = await onSaveList([...options.filter((o) => o !== v), v]);
    if (saved) {
      setDraft("");
      // pick it once Radix has registered the new option (same-tick select empties the value)
      setTimeout(() => onChange(v), 0);
    }
  };

  const remove = async (o) => {
    const next = options.filter((x) => x !== o);
    if (!next.length) return onSaveList(null, minOne);
    const saved = await onSaveList(next);
    if (saved && value === o) onChange(saved[0]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-widest text-zinc-400">{label}</Label>
        <button
          type="button"
          data-testid={`slab-${testKey}-manage`}
          onClick={() => setEditing((v) => !v)}
          className="text-[10px] font-bold uppercase tracking-widest text-amber-300 transition-colors duration-150 hover:text-amber-200"
        >
          {editing ? "Done" : "Edit list"}
        </button>
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={`slab-${testKey}-select`} className="h-12 border-zinc-800 bg-zinc-900 text-base">
          <SelectValue>{value}</SelectValue>
        </SelectTrigger>
        <SelectContent className="border-zinc-800 bg-zinc-900">
          {options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`slab-${testKey}-${o}`}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {editing && (
        <div className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950 p-2" data-testid={`slab-${testKey}-editor`}>
          <div className="flex flex-wrap gap-1.5">
            {options.map((o) => (
              <span key={o} className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2 py-0.5 text-xs text-zinc-200">
                {o}
                <button
                  type="button"
                  data-testid={`slab-${testKey}-remove-${o}`}
                  onClick={() => remove(o)}
                  title={`Remove ${o}`}
                  className="text-zinc-500 transition-colors duration-150 hover:text-red-400"
                >
                  <X size={11} weight="bold" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              data-testid={`slab-${testKey}-new`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder={placeholder}
              className="h-9 border-zinc-800 bg-zinc-900 text-sm"
            />
            <Button
              type="button"
              data-testid={`slab-${testKey}-add`}
              onClick={add}
              disabled={!draft.trim()}
              className="h-9 bg-indigo-500 px-3 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={12} weight="bold" className="mr-1" /> Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
